export default {
    title: 'Calibration',
    subtitle:
        'Spot rating-severity gaps between {{managerPluralLabel}} and confirm that evaluations are fair.',
    cycle: {
        selectLabel: 'Cycle',
        endDateLabel: 'Ends on {{date}}',
        noEndDate: 'No end date',
        empty: {
            title: 'No cycle available',
            message: 'No cycle has a submitted evaluation to calibrate.',
        },
    },
    views: {
        groupLabel: 'Report view',
        managers: {
            title: 'View by {{managerLabel}}',
            description: "Compare each {{managerLabel}}'s average rating level",
        },
        employees: {
            title: 'Employee calibration',
            description: "Compare each employee's average score against the cohort",
        },
    },
    pivots: {
        ratingLevel: 'Rating level',
        differentiation: 'Differentiation',
    },
    summary: {
        empty: {
            title: 'No data for this cycle',
            message: 'No evaluated employee matches the selected filters.',
        },
    },
    filters: {
        reset: 'Reset',
    },
    deviation: {
        ariaLabel: "Each {{managerLabel}}'s rating deviation from the cohort",
        intro: "This chart shows whether some {{managerPluralLabel}} rate more severely or more leniently than your organization's average. Treat it as a starting point: understand the context of each evaluation before drawing conclusions from these numbers.",
        moreSevere: 'More severe',
        moreLenient: 'More lenient',
        cohort: 'Cohort',
        offAxis: 'off axis',
        notRepresentative: 'Not representative',
        notRepresentativeTooltip: 'Fewer than {{count}} people evaluated',
        notRepresentativeTooltip_one: 'Fewer than {{count}} person evaluated',
        notRepresentativeTooltip_other: 'Fewer than {{count}} people evaluated',
        evaluatedCount: '{{count}} people',
        evaluatedCount_one: '{{count}} person',
        evaluatedCount_other: '{{count}} people',
        legendAbove: 'Rating above the rest of the cohort',
        legendBelow: 'Rating below',
        cohortAverage: 'Cohort average: {{score}} / {{max}}',
        evaluatedTotal: '{{count}} evaluated',
        evaluatedTotal_one: '{{count}} evaluated',
        evaluatedTotal_other: '{{count}} evaluated',
        managerTotal: '{{count}} {{managerPluralLabel}}',
        managerTotal_one: '{{count}} {{managerLabel}}',
        managerTotal_other: '{{count}} {{managerPluralLabel}}',
    },
    differentiation: {
        ariaLabel: 'Distribution of employee scores by {{managerLabel}}',
        title: 'Are they differentiating?',
        intro: "A {{managerLabel}}'s evaluations should generally show some spread. When they all sit very close to their average, a conversation may be warranted.",
        pointLabel: 'One person',
        markerLabel: "{{managerLabelCapitalized}}'s average",
        rangeLabel: '{{min}} to {{max}}',
        spreadLabel: 'Spread: {{spread}} pts',
        sortLabel: 'Sort {{managerPluralLabel}}',
        sortByScoreDesc: 'Highest score',
        sortByScoreAsc: 'Lowest score',
        sortBySpreadDesc: 'Widest spread',
        sortBySpreadAsc: 'Narrowest spread',
        sortByName: 'Name',
    },
    draft: {
        message:
            'Your changes are currently a draft. When you are ready, you can push them to production.',
        emptyMessage: 'No draft changes for this cycle.',
    },
    table: {
        columns: {
            person: 'Person',
            manager: '{{managerLabel}}',
            roleAttainment: 'Role attainment',
            givenLevel: "{{managerLabelCapitalized}}'s score",
            calibratedScore: 'Score',
            calibratedLevel: 'Calibrated',
            comment: 'Comment',
        },
        status: {
            draft: 'Draft',
            published: 'Published',
            none: '—',
        },
        fieldLabel: {
            calibratedScore: 'Calibrated score for {{name}}',
            calibratedLevel: 'Calibrated level for {{name}}',
            comment: 'Calibration comment for {{name}}',
        },
        noChange: 'No change',
        levelChanged: 'Level differs from the one the {{managerLabel}} gave',
        percentSymbol: '%',
        regionLabel: 'Employee calibration table',
        search: 'Search an employee',
        empty: 'No employee matches the search.',
        saving: 'Saving…',
        saved: 'Saved',
        error: 'Save failed',
        commentPlaceholder: 'Add a note',
    },
    bulk: {
        actionLabel: 'Bulk edit',
        disabledTooltip: 'No evaluated employee to adjust',
        modalTitle: 'Bulk edit',
        managersLabel: '{{managerPluralLabelCapitalized}}',
        managersAllSelected: 'Every {{managerLabel}} in scope',
        directionLabel: 'Direction',
        directionIncrease: 'Increase',
        directionDecrease: 'Decrease',
        percentageLabel: 'Percentage',
        percentageHint: 'Between {{min}} and {{max}}%',
        previewLabel: 'Preview',
        previewHint: 'Enter a percentage to preview the sweep.',
        affectedCount: '{{count}} people affected',
        affectedCount_one: '{{count}} person affected',
        affectedCount_other: '{{count}} people affected',
        confirm: 'Apply',
        cancel: 'Cancel',
        success: 'Sweep applied: {{count}} scores updated.',
        success_one: 'Sweep applied: {{count}} score updated.',
        success_other: 'Sweep applied: {{count}} scores updated.',
    },
    publish: {
        actionLabel: 'Apply my changes',
        disabledTooltip: 'No draft to publish',
        modalTitle: 'Apply my changes',
        confirmationDraftCount: '{{count}} draft calibrations will be published.',
        confirmationDraftCount_one: '{{count}} draft calibration will be published.',
        confirmationDraftCount_other: '{{count}} draft calibrations will be published.',
        confirmationCycle: 'Cycle: {{cycle}}',
        confirmationIrreversible: 'This permanently replaces the previous publication.',
        confirmationNoNotification:
            'No notification will be sent to employees or {{managerPluralLabel}}.',
        acknowledgeCheckbox: 'I understand',
        confirm: 'Apply my changes',
        cancel: 'Cancel',
        success: '{{count}} calibrations published.',
        success_one: '{{count}} calibration published.',
        success_other: '{{count}} calibrations published.',
    },
    disabled: {
        title: 'Feature not enabled',
        message: 'This feature is not enabled for your organization.',
    },
    errors: {
        loadCycles: {
            title: 'Cycles could not be loaded',
            message: 'The calibration cycle list is not responding. Reload the page in a moment.',
        },
        loadReport: {
            title: 'Report could not be loaded',
            message:
                'The calibration report is not responding. Reload the page or switch to another cycle.',
        },
        bulkAdjustmentFailed: 'The sweep could not be applied.',
        publishFailed: 'The calibrations could not be published.',
    },
};
